import {
  CalendarTodayOutlined as CalendarTodayOutlinedIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { Box, Chip, Link as MuiLink, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { GcfComponentSection } from 'src/components/gcfComponentSection';
import { PreviewModal } from 'src/components/modal/previewModal';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { US_DATE_FORMAT } from 'src/constants';
import { IScheduledSectionComponent } from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.interface';
import { ComponentTypes, IComponent } from 'src/interfaces';
import { lang } from 'src/utils';
import { FileTypes, getFileExtensionType } from 'src/utils/file-types';
import { getSectionPreviewData } from 'src/utils/gcf-helper';

import {
  StyledBackupBox,
  StyledBackupItemContentBox,
  StyledBackupItemStack,
  StyledDateInnerStack,
  StyledDateLabelTypography,
  StyledDateRangeStack,
  StyledHeaderStack,
  StyledImageBox,
  StyledMainBox,
  StyledMetadataStack,
} from './SidePanelItem.styled';

export interface ISidePanelItem extends IScheduledSectionComponent {
  screenIndex: number;
  position: string;
}

interface ISidePanelItemProps {
  item: ISidePanelItem;
}

export const SidePanelItem = (props: ISidePanelItemProps) => {
  const { item } = props;
  const enabledOverride = !!item.allow_overrides;

  const [selectedBackupComponent, setSelectedBackupComponent] =
    useState<IComponent>();

  return (
    <StyledMainBox>
      <StyledHeaderStack>
        <StyledImageBox>
          <GcfComponentSection
            fullWidth
            assemblyComponents={[
              {
                component: {
                  type: item.component?.component_type?.name,
                  config: item.component?.config,
                  name: item.component?.name,
                  width: item.component?.width,
                  height: item.component?.height,
                  theme: item.component?.theme?.name,
                },
              },
            ]}
            height={item?.component?.height ?? 0}
            width={item?.component?.width ?? 0}
            scale={0.05}
            previewData={getSectionPreviewData(
              item?.component?.component_type?.name as ComponentTypes,
            )}
          />
        </StyledImageBox>

        <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-word' }}>
          {item?.component?.name}
        </Typography>
      </StyledHeaderStack>

      {/* Metadata Chips */}
      <StyledMetadataStack>
        <Chip
          label={`${lang.screen} ${Number(item.screenIndex || 0) + 1}`}
          size="small"
        />
        <Chip label={`${lang.position} ${item.position}`} size="small" />
        {enabledOverride && (
          <Chip
            icon={<GroupIcon />}
            label={lang.cmOverrideEnabled}
            size="small"
          />
        )}
      </StyledMetadataStack>

      {/* Date Range */}
      <StyledDateRangeStack>
        <Box flex={1}>
          <StyledDateLabelTypography variant="body1">
            {lang.startDate}
          </StyledDateLabelTypography>
          <StyledDateInnerStack>
            <CalendarTodayOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              {item?.scheduleStartDate
                ? dayjs(item?.scheduleStartDate).format(US_DATE_FORMAT)
                : ''}
            </Typography>
          </StyledDateInnerStack>
        </Box>

        <Box flex={1}>
          <StyledDateLabelTypography variant="body1">
            {lang.endDate}
          </StyledDateLabelTypography>
          <StyledDateInnerStack>
            <CalendarTodayOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              {item?.scheduleEndDate
                ? dayjs(item.scheduleEndDate).format(US_DATE_FORMAT)
                : lang.noEndDate}
            </Typography>
          </StyledDateInnerStack>
        </Box>
      </StyledDateRangeStack>

      {/* Backup / Components List */}
      {!!item?.backups && (
        <StyledBackupBox>
          {item?.backups?.map((backup, index) => {
            const isVideo =
              getFileExtensionType(backup.component?.config?.background) ===
              FileTypes.VIDEO;

            const backupLabel = `${(
              Number(backup.duration ?? (backup?.config?.duration || 0)) / 1000
            )
              .toFixed(1)
              .replace(/[.,]0$/, '')}s`;

            return (
              <StyledBackupItemStack
                key={backup.id || backup.uid}
                $isLast={index === (item?.backups?.length ?? 0) - 1}>
                <StyledBackupItemContentBox>
                  <CustomTooltip
                    title={backup.component?.name}
                    cursor="pointer"
                    skipValidateOverflow>
                    <MuiLink
                      onClick={() => {
                        setSelectedBackupComponent(backup.component);
                      }}>
                      {backup.component?.name}
                    </MuiLink>
                  </CustomTooltip>
                </StyledBackupItemContentBox>

                {isVideo && (
                  <Chip sx={{ flex: 0.1 }} label={backupLabel} size="small" />
                )}
              </StyledBackupItemStack>
            );
          })}
        </StyledBackupBox>
      )}

      {!!selectedBackupComponent && (
        <PreviewModal
          open={!!selectedBackupComponent}
          onClose={() => setSelectedBackupComponent(undefined)}
          content={selectedBackupComponent}
          type="component"
          hideButtons
        />
      )}
    </StyledMainBox>
  );
};
